import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import NotificationDialog from 'components/common/NotificationDialog';
import { getReleaseNotes } from 'constants/releaseNotes';

type VersionChangeDetail = {
  newVersion?: string;
  previousVersion?: string;
};

type DialogState = {
  open: boolean;
  version: string;
  notes: string[] | null;
};

const fallbackNote =
  'A new version of Rollo Ship is available. Reload to stay on the latest experience.';

const useStyles = makeStyles((theme) =>
  createStyles({
    notesList: {
      margin: 0,
      paddingLeft: theme.spacing(2.5),
    },
    noteItem: {
      '&:not(:last-child)': {
        marginBottom: theme.spacing(2),
      },
    },
  })
);

const ReleaseNotesDialog = () => {
  const classes = useStyles();
  const [state, setState] = useState<DialogState>({
    open: false,
    version: '',
    notes: null,
  });

  const handleClose = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    const handleVersionChange = (event: Event) => {
      const detail = (event as CustomEvent<VersionChangeDetail>).detail || {};

      const nextVersion = detail.newVersion;

      if (!nextVersion) return;

      setState((prevState) => {
        if (prevState.open && prevState.version === nextVersion) {
          return prevState;
        }

        return {
          open: true,
          version: nextVersion,
          notes: getReleaseNotes(nextVersion),
        };
      });
    };

    window.addEventListener('rolloVersionChange', handleVersionChange);

    return () => {
      window.removeEventListener('rolloVersionChange', handleVersionChange);
    };
  }, []);

  if (!state.open) return null;

  return (
    <NotificationDialog
      open={state.open}
      title="New version available"
      confirmText="OK"
      onCancel={handleClose}
      onConfirm={handleClose}>
      <Typography variant="subtitle1" gutterBottom>
        {state.version
          ? `Rollo Ship has been updated to version ${state.version}.`
          : 'Rollo Ship has been updated.'}
      </Typography>
      {state.notes?.length ? (
        <ul className={classes.notesList}>
          {state.notes.map((note) => (
            <Typography key={note} component="li" variant="body2" className={classes.noteItem}>
              {note}
            </Typography>
          ))}
        </ul>
      ) : (
        <Typography variant="body2">{fallbackNote}</Typography>
      )}
    </NotificationDialog>
  );
};

export default ReleaseNotesDialog;
